import * as qs from 'qs';

import { apiUrl } from '@/env';
import {
  CompanyDepartmentCreateRequestBody,
  CompanyDepartmentCreateResponse,
  CompanyDepartmentDeletionImpact,
  CompanyDepartmentMoveRequestBody,
  CompanyDepartmentUpdateRequestBody,
  CompanyDepartmentUpdateResponse,
  CompanySdsCountResponse,
  CompanySdsDataResponse,
  CompanySdsIndexResponse,
  CompanySdsShowResponse,
  CompanyShowResponse,
  CompanyUpdateRequestBody,
  CompanyUpdateRequestParams,
  CompanyUserCreateRequestBody,
  CompanyUserCreateResponse,
  CompanyWhitelistIpCreateRequestBody,
  CompanyWhitelistIpCreateResponse,
  CompanyWhitelistIpUpdateNameRequestBody,
  CompanyWhitelistIpBulkCreateRequestBody,
  CompanyWhitelistIpBulkCreateResponse,
  CheckDuplicateSdsesRequestBody,
  CreateCompanySdsRequestBody,
  CreateCompanySdsRequestUnit,
  CreateAttachmentRequestUnit,
  DuplicatedSdsesResponse,
  DeleteCompanySdsesRequestBody,
  UpdateCompanySdsRequestBody,
  DownloadCompanySdsesCSVRequestQuery,
  DownloadRiskAssessmentRecomputeCSVRequestQuery,
  UploadRiskAssessmentRecomputeCSVResponse,
  CompanyTagCreateRequestBody,
  CompanyTagCreateResponse,
  CompanyTagUpdateRequestBody,
  CompanyTagBulkUploadRequestBody,
  CompanyTagBulkUploadResponse,
  UpdateCompanySdsesRequestBody,
  UpdateCompanyUserRequestBody,
  UpdateReconfirmationSdsRequestBody,
  GetCompanySdsesRequestQuery,
  ExportCompanySdsesRequest,
  RiskAssessmentExportRequest,
  GetCompanySdsesCountRequestQuery,
  UploadCompanySdsesCSVRequestBody,
  DownloadCompanySdsesPDFRequestQuery,
  GetCompanyExportOperationsResponse,
  UploadCompanySdsesCSVResponse,
  CompanyWhiteListIpGetResponse,
  GetCompanyPeriodicExportSetting,
  GetCompanyPeriodicExportSettingDetail,
  CreateCompanyPeriodicExportSettingRequest,
  UpdateCompanyPeriodicExportSettingRequest,
  GetSendableCompanyUser,
  GetUserSearchCriteriaResponse,
  PostUserSearchCriteriaResponse,
  PutUserSearchCriteriaResponse,
  CompanySettingsUpdateRequestBody,
  GetEditHistoriesResponse,
  UpdatePasswordSettingRequest,
  PasswordSettingGetResponse,
  Enforce2FASetting,
  User2FAEnrollmentResponse,
} from '@/interfaces/api/general/company';
import { DepartmentShowResponse } from '@/interfaces/api/general/department';
import {
  DepartmentCsvUploadResult,
  DepartmentTreeNode,
} from '@/interfaces/model/general/department';
import {
  CreateDynamicListRequestBody,
  UpdateDynamicListRequestBody,
  DynamicListResultItem,
  GetDynamicList,
  GetDynamicListResults,
  GetDynamicListResultsRequestBody,
  GetDynamicLists,
  RequestDynamicListRequestBody,
} from '@/interfaces/api/general/dynamicList';
import { DynamicListResultRequestBody } from '@/interfaces/api/general/dynamicListResult';
import {
  DynamicListTemplate,
  GetDynamicListTemplatesResponse,
  GetDynamicListTemplatesRequestQuery,
  CreateDynamicListFromTemplateRequestBody,
} from '@/interfaces/api/general/dynamicListTemplate';
import { GetFeatureFlagListResponse } from '@/interfaces/api/general/feature_flag';
import {
  InquiryRequestBody,
  QuotationRequestBody,
} from '@/interfaces/api/general/info';
import { Law, GetLawsRequestQuery } from '@/interfaces/api/general/law';
import {
  Regulation,
  GetRegulationsRequestQuery,
} from '@/interfaces/api/general/regulation';
import {
  GetSupplierInquiryRequestsQuery,
  SupplierInquiryCreateRequest,
  SupplierInquiryCreateResponse,
  SupplierInquiryListResponse,
  SupplierInquiryQuotaResponse,
  SupplierInquiryResponse,
  SupplierInquiryScreeningSummaryResponse,
  SupplierInquirySummaryResponse,
} from '@/interfaces/api/general/supplierInquiry';
import { SignUpRequestBody } from '@/interfaces/api/general/signIn';
import {
  CompanyUsersShowResponse,
  GetCompanyPasswordLengthRequest,
  GetCompanyPasswordLengthResponse,
  UserShowResponse,
  UserUpdateRequestBody,
  UserUpdateResponse,
} from '@/interfaces/api/general/user';
import {
  MSdsSection2Category,
  MSdsSection2Hazard,
  MSdsSection2Item,
} from '@/interfaces/api/general/master';
import axios, { CancelTokenSource, InternalAxiosRequestConfig } from 'axios';
import store from '@/store';
import { readUserLocale } from '@/store/main/getters';
import {
  UserLanguageUpdate,
  UserLanguageResponse,
} from '@/interfaces/api/general/language';
import { SdsesFilterParams } from '@/composables/useSdsFilter';
import clerk from '@/lib/clerk';

let cancelTokenSource: CancelTokenSource | null = null;

const injectLocale = async (config: InternalAxiosRequestConfig) => {
  config.headers.set('locale', readUserLocale(store));
  return config;
};

const api = axios.create({
  baseURL: `${apiUrl}/api/v1`,
});
const publicApi = axios.create({
  baseURL: `${apiUrl}/api/v1`,
});

api.interceptors.request.use(injectLocale, (error) => Promise.reject(error));
api.interceptors.request.use(async (config) => {
  const token = await clerk.session?.getToken({ template: 'with_external_id' });
  const isLoggedIn = clerk.session?.status === 'active';
  if (!token || !isLoggedIn) {
    // Refresh the page to reinitialize auth
    // and redirect the user to the login page.
    sessionStorage.setItem(
      'loginState',
      JSON.stringify({ tokenInvalidated: true })
    );
    window.location.reload();
  }
  config.headers.set('Authorization', `Bearer ${token}`);

  // テナント切り替え対応: 選択中のテナントIDをヘッダーで送信
  const selectedCompanyId = localStorage.getItem('selectedCompanyId');
  if (selectedCompanyId) {
    config.headers.set('X-Company-Id', selectedCompanyId);
  }

  return config;
});
publicApi.interceptors.request.use(injectLocale, (error) =>
  Promise.reject(error)
);

export const generalApi = {
  async inquiry(data: InquiryRequestBody) {
    return publicApi.post('/info/inquiry', data);
  },
  async inquiryEn(data: InquiryRequestBody) {
    return publicApi.post('/info/en/inquiry', data);
  },
  async quotation(data: QuotationRequestBody) {
    return publicApi.post('/info/quotation', data);
  },
  async signUp(data: SignUpRequestBody) {
    return publicApi.post('/signup', data);
  },
  async getCompany(companyId: string) {
    return api.get<CompanyShowResponse>(`/companies/${companyId}`);
  },
  async updateCompany(
    params: CompanyUpdateRequestParams,
    payload: CompanyUpdateRequestBody
  ) {
    return api.put(`/companies/${params.companyId}`, payload);
  },
  async updateCompanySettings(
    params: CompanyUpdateRequestParams,
    payload: CompanySettingsUpdateRequestBody
  ) {
    return api.put(`/companies/${params.companyId}/settings`, payload);
  },
  async deleteCompany(companyId: string) {
    return api.delete(`/companies/${companyId}`);
  },
  async getCompanyDepartments(companyId: string) {
    return api.get<DepartmentShowResponse>(
      `/companies/${companyId}/departments`
    );
  },
  async getCompanyDepartmentTree(companyId: string) {
    return api.get<DepartmentTreeNode[]>(
      `/companies/${companyId}/departments/tree`
    );
  },
  async getCompanyDepartmentDeletionImpact(
    companyId: string,
    departmentId: string
  ) {
    return api.get<CompanyDepartmentDeletionImpact>(
      `/companies/${companyId}/departments/${departmentId}/deletion-impact`
    );
  },
  async createCompanyDepartment(
    companyId: string,
    data: CompanyDepartmentCreateRequestBody
  ) {
    return api.post<CompanyDepartmentCreateResponse>(
      `/companies/${companyId}/departments`,
      data
    );
  },
  async updateCompanyDepartment(
    companyId: string,
    departmentId: string,
    data: CompanyDepartmentUpdateRequestBody
  ) {
    return api.put<CompanyDepartmentUpdateResponse>(
      `/companies/${companyId}/departments/${departmentId}`,
      data
    );
  },
  async moveCompanyDepartment(
    companyId: string,
    departmentId: string,
    data: CompanyDepartmentMoveRequestBody
  ) {
    return api.patch<DepartmentTreeNode[]>(
      `/companies/${companyId}/departments/${departmentId}/position`,
      data
    );
  },
  async deleteCompanyDepartment(companyId: string, departmentId: string) {
    return api.delete(`/companies/${companyId}/departments/${departmentId}`);
  },
  async getCompanyDepartmentsCsv(companyId: string, lang?: string) {
    const query = lang ? qs.stringify({ lang }) : '';
    return api.get(
      `/companies/${companyId}/departments/csv${query ? `?${query}` : ''}`
    );
  },
  async postCompanyDepartmentsCsv(
    companyId: string,
    data: FormData,
    lang?: string
  ) {
    const query = lang ? qs.stringify({ lang }) : '';
    const url = `/companies/${companyId}/departments/csv${
      query ? `?${query}` : ''
    }`;
    return api.post<DepartmentCsvUploadResult>(url, data);
  },

  async updateCompanyUser(
    companyId: string,
    userId: string,
    data: UpdateCompanyUserRequestBody
  ) {
    return api.put(`/companies/${companyId}/users/${userId}`, data);
  },

  async resetCompanyUserPassword(companyId: string, userId: string) {
    return api.post(
      `/companies/${companyId}/users/${userId}/reset-password`,
      null
    );
  },

  async getMe() {
    return api.get<UserShowResponse>('/users/me');
  },
  async updateMe(data: UserUpdateRequestBody) {
    return api.put<UserUpdateResponse>('/users/me', data);
  },
  async getCompanyPasswordLength(data: GetCompanyPasswordLengthRequest) {
    const query = qs.stringify(data, { indices: false });
    return publicApi.get<GetCompanyPasswordLengthResponse>(
      `/users/me/company/password_length?${query}`
    );
  },
  async logoutSAMLProxy() {
    return api.post('/logout/saml/proxy');
  },
  async passwordRecovery(email: string) {
    return publicApi.post(`/password-recovery/${email}`);
  },
  async validatePassword(email: string, password: string) {
    return publicApi.post('/password/validation', {
      email: email,
      password: password,
    });
  },
  // Whether the user opted into email 2FA (Clerk unsafe_metadata). Kept as a
  // dedicated endpoint (not merged into /password/validation) so the
  // password-policy check keeps a single responsibility. Called during login
  // to decide whether to reset Client Trust before sign-in.
  async getEmail2faStatus(email: string) {
    return publicApi.post<{ email_2fa_enabled: boolean }>('/email-2fa-status', {
      email: email,
    });
  },
  // 失敗ログインを監査に記録する（fire-and-forget・best-effort・未認証EP）。
  // Clerk のエラーコード(getClerkErrorCode の値)をそのまま送り、backend が
  // email→tenant を解決して auth.login_failed を emit する。応答は無視し、
  // 失敗しても呼び出し側のログインフローに影響させない。
  // V2(frontend_next: lib/api/endpoints reportLoginFailed) のミラー。
  async reportLoginFailed(email: string, code: string): Promise<void> {
    try {
      await publicApi.post('/auth-events/login-failed', { email, code });
    } catch {
      // 監査記録の失敗はユーザー体験に影響させない
    }
  },
  async resetPassword(passwordResetToken: string, password: string) {
    return publicApi.post('/reset-password/', {
      new_password: password,
      token: passwordResetToken,
    });
  },
  async verifyToken(token: string): Promise<void> {
    return publicApi.post('/verify-token', {
      token,
    });
  },
  async regenerateActivationMail(data: { email: string }) {
    return publicApi.post('/activation/regeneration', data);
  },
  async activateUser(data: { token: string }) {
    return publicApi.put('/signup/activation', data);
  },
  async getCompanyUsers(
    companyId: string,
    options: { fetchAuthInfo?: boolean } = {}
  ) {
    const { fetchAuthInfo = false } = options;
    return api.get<CompanyUsersShowResponse>(
      `/companies/${companyId}/users?fetchAuthInfo=${fetchAuthInfo}`
    );
  },
  async getCompanyUsersWithSwitchable(
    companyId: string,
    options: { fetchAuthInfo?: boolean } = {}
  ) {
    const { fetchAuthInfo = false } = options;
    return api.get<CompanyUsersShowResponse>(
      `/companies/${companyId}/users/with-switchable?fetchAuthInfo=${fetchAuthInfo}`
    );
  },
  async getCompanyUserCsv(companyId: string, locale = 'ja') {
    const query = qs.stringify({ locale });
    return api.get(`/companies/${companyId}/users/csv/download?${query}`);
  },
  async postCompanyUserCsv(companyId: string, data: FormData, locale?: string) {
    const query = locale ? qs.stringify({ locale }) : '';
    const url = `/companies/${companyId}/users/csv/upload${
      query ? `?${query}` : ''
    }`;
    return api.post(url, data);
  },

  async createCompanyUser(
    companyId: string,
    data: CompanyUserCreateRequestBody
  ) {
    return api.post<CompanyUserCreateResponse>(
      `/companies/${companyId}/users`,
      data
    );
  },

  async deleteCompanyUser(companyId: string, email: string) {
    return api.delete(`/companies/${companyId}/users/${email}`);
  },
  async deactivateCompanyUser(companyId: string, userUuid: string) {
    return api.patch(`/companies/${companyId}/users/${userUuid}/deactivate`);
  },
  async getCompanyUploadUsers(companyId: string) {
    return api.get<Array<UserShowResponse>>(`/companies/${companyId}/users`);
  },
  async getCompanySds(companyId: string, sdsId: string) {
    return api.get<CompanySdsShowResponse>(
      `/companies/${companyId}/sdses/${sdsId}`
    );
  },
  async getUserSearchCriteria(companyId: string, userId: string) {
    return api.get<GetUserSearchCriteriaResponse>(
      `/companies/${companyId}/users/${userId}/search-criteria`
    );
  },
  async postUserSearchCriteria(data: {
    userId: string;
    companyId: string;
    name: string;
    criteria: SdsesFilterParams;
  }) {
    return api.post<PostUserSearchCriteriaResponse>(
      `/companies/${data.companyId}/users/${data.userId}/search-criteria`,
      {
        name: data.name,
        criteria: data.criteria,
      }
    );
  },
  async putUserSearchCriteria(data: {
    companyId: string;
    userId: string;
    searchCriteriaId: string;
    name: string;
  }) {
    return api.put<PutUserSearchCriteriaResponse>(
      `/companies/${data.companyId}/users/${data.userId}/search-criteria/${data.searchCriteriaId}`,
      {
        name: data.name,
      }
    );
  },
  async deleteUserSearchCriteria(data: {
    companyId: string;
    userId: string;
    searchCriteriaId: string;
  }) {
    return api.delete(
      `/companies/${data.companyId}/users/${data.userId}/search-criteria/${data.searchCriteriaId}`
    );
  },

  async getCompanySdses(
    companyId: string,
    data: GetCompanySdsesRequestQuery,
    signal?: AbortSignal
  ) {
    const { sortState, ...body } = data;
    const query = qs.stringify(
      {
        ...body,
        order_by_created_at: sortState?.created_at,
        order_by_revised_at: sortState?.revised_at,
        order_by_name: sortState?.name,
        order_by_status: sortState?.status,
        order_by_assessed_at: sortState?.assessed_at,
        order_by_supplier_confirmed_at: sortState?.supplier_confirmed_at,
      },
      {
        indices: false,
      }
    );

    return api.get<CompanySdsIndexResponse>(
      `/companies/${companyId}/sdses?${query}`,
      { signal }
    );
  },

  async getCompanySdsesCount(
    companyId: string,
    data: GetCompanySdsesCountRequestQuery,
    signal?: AbortSignal
  ) {
    const query = qs.stringify(data, { indices: false });

    return api.get<CompanySdsCountResponse>(
      `/companies/${companyId}/sdses/count?${query}`,
      { signal }
    );
  },
  async getCompanySdsPdf(companyId: string, sdsUuid: string) {
    return api.get<CompanySdsDataResponse>(
      `/companies/${companyId}/sds/${sdsUuid}/pdf`
    );
  },

  async getCompanySdsAttachmentPdf(
    companyId: string,
    sdsUuid: string,
    attachmentUuid: string
  ) {
    return api.get<CompanySdsDataResponse>(
      `/companies/${companyId}/sdses/${sdsUuid}/attachments/${attachmentUuid}`
    );
  },

  // 交換フォーマットは1製品=1ドキュメントのため、複数製品SDSはZIPで返る
  async getCompanySdsJson(companyId: string, sdsUuid: string) {
    return api.get<Blob>(
      `/companies/${companyId}/sdses/${sdsUuid}/json/download`,
      {
        responseType: 'blob',
      }
    );
  },

  async exportCompanySdsJsonToPdf(companyId: string, data: FormData) {
    // 本番構成では署名URLのJSON、ローカル構成ではPDFバイトが返るため blob で受ける
    return api.post<Blob>(`/companies/${companyId}/sdses/json_to_pdf`, data, {
      responseType: 'blob',
    });
  },

  async getCompanyExportOperations(companyId: string, language?: string) {
    const params = language ? { language } : {};
    return api.get<GetCompanyExportOperationsResponse>(
      `/companies/${companyId}/sdses/export/operations`,
      { params }
    );
  },

  async exportCompanySdses(companyId: string, data: ExportCompanySdsesRequest) {
    return api.post<string>(`/companies/${companyId}/sdses/export`, data);
  },

  async riskAssessmentExport(
    companyId: string,
    data: RiskAssessmentExportRequest
  ) {
    return api.post<string>(
      `/companies/${companyId}/sdses/risk-assessment-export`,
      data
    );
  },

  async getGcsSignedUrl(
    companyId: string,
    sdsList: Array<CreateCompanySdsRequestUnit>
  ) {
    return api.post<Array<CreateCompanySdsRequestUnit>>(
      `/companies/${companyId}/get_signed_url`,
      sdsList
    );
  },

  async getAttachmentGcsSignedUrl(
    companyId: string,
    sdsId: string,
    attachments: Array<CreateAttachmentRequestUnit>
  ) {
    return api.post<Array<CreateAttachmentRequestUnit>>(
      `/companies/${companyId}/sdses/${sdsId}/attachments/get_signed_url`,
      attachments
    );
  },

  async createSdses(companyId: string, data: CreateCompanySdsRequestBody) {
    return api.post(`/companies/${companyId}/sdses`, data);
  },

  async checkSdsesDuplicates(
    companyId: string,
    data: CheckDuplicateSdsesRequestBody
  ) {
    return api.post<DuplicatedSdsesResponse>(
      `/companies/${companyId}/sdses/duplicate-check`,
      data
    );
  },

  async updateCompanySdses(
    companyId: string,
    data: UpdateCompanySdsesRequestBody
  ) {
    return api.put(`/companies/${companyId}/sdses`, data);
  },

  async deleteCompanySdses(
    companyId: string,
    data: DeleteCompanySdsesRequestBody
  ) {
    return api.put(`/companies/${companyId}/sdses/delete`, data);
  },

  async downloadPdfCount(companyId: string, signal?: AbortSignal) {
    return api.get(`/companies/${companyId}/sdses/download/pdf_count`, {
      signal,
    });
  },

  async downloadCompanySdsesPDFBulk(
    companyId: string,
    data: DownloadCompanySdsesPDFRequestQuery
  ) {
    return api.post(`/companies/${companyId}/sdses/download/bulk`, data);
  },

  async downloadCompanySdsesCSV(
    companyId: string,
    data: DownloadCompanySdsesCSVRequestQuery
  ) {
    const query = qs.stringify(data, { indices: false });
    return api.get<Blob>(`/companies/${companyId}/sdses/download/csv?${query}`);
  },

  async downloadRiskAssessmentRecomputeCSV(
    companyId: string,
    data: DownloadRiskAssessmentRecomputeCSVRequestQuery
  ) {
    const query = qs.stringify(data, { indices: false });
    return api.get<string>(
      `/companies/${companyId}/sdses/risk-assessment/recompute/csv?${query}`
    );
  },

  async uploadRiskAssessmentRecomputeCSV(companyId: string, data: FormData) {
    return api.post<UploadRiskAssessmentRecomputeCSVResponse>(
      `/companies/${companyId}/sdses/risk-assessment/recompute/csv`,
      data
    );
  },

  async downloadGhsLabel(
    companyId: string,
    sdsId: string,
    data: Record<string, unknown>
  ) {
    return api.post(`/companies/${companyId}/sdses/${sdsId}/label`, data);
  },

  async uploadCompanySdsesCSV(
    companyId: string,
    data: UploadCompanySdsesCSVRequestBody
  ) {
    return api.patch<UploadCompanySdsesCSVResponse>(
      `/companies/${companyId}/sdses/batch_patch`,
      data
    );
  },

  async updateCompanySds(
    companyId: string,
    sdsId: string,
    formData: UpdateCompanySdsRequestBody
  ) {
    return api.put(`/companies/${companyId}/sdses/${sdsId}`, formData);
  },

  async updateReconfirmationSds(
    companyId: string,
    sdsId: string,
    formData: UpdateReconfirmationSdsRequestBody
  ) {
    return api.put(
      `/companies/${companyId}/sdses/${sdsId}/reconfirmation_sds`,
      formData
    );
  },

  async getCompanyWhitelistIp(companyId: string) {
    return api.get<unknown, CompanyWhiteListIpGetResponse>(
      `/companies/${companyId}/whitelist_ips`
    );
  },

  async createCompanyWhitelistIp(
    companyId: string,
    data: CompanyWhitelistIpCreateRequestBody
  ) {
    return api.post<CompanyWhitelistIpCreateResponse>(
      `/companies/${companyId}/whitelist_ips`,
      data
    );
  },

  async createCompanyWhitelistIpBulk(
    companyId: string,
    data: CompanyWhitelistIpBulkCreateRequestBody
  ) {
    return api.post<CompanyWhitelistIpBulkCreateResponse>(
      `/companies/${companyId}/whitelist_ips/bulk_register`,
      data
    );
  },

  async updateCompanyWhitelistIpName(
    companyId: string,
    ipAddress: string,
    data: CompanyWhitelistIpUpdateNameRequestBody
  ) {
    const encodedIpAddress = encodeURIComponent(ipAddress);
    return api.patch(
      `/companies/${companyId}/whitelist_ips/${encodedIpAddress}`,
      data
    );
  },

  async deleteCompanyWhitelistIp(companyId: string, ipAddress: string) {
    const encodedIpAddress = encodeURIComponent(ipAddress);
    return api.delete(
      `/companies/${companyId}/whitelist_ips/${encodedIpAddress}`
    );
  },

  async getPasswordSetting(companyId: string) {
    return api.get<PasswordSettingGetResponse>(
      `/companies/${companyId}/password/setting`
    );
  },

  async updatePasswordSetting(
    companyId: string,
    data: UpdatePasswordSettingRequest
  ) {
    return api.put<PasswordSettingGetResponse>(
      `/companies/${companyId}/password/setting`,
      data
    );
  },

  async getEnforce2Fa(companyId: string) {
    return api.get<Enforce2FASetting>(`/companies/${companyId}/enforce_2fa`);
  },

  async updateEnforce2Fa(companyId: string, data: Enforce2FASetting) {
    return api.post(`/companies/${companyId}/enforce_2fa`, data);
  },

  async get2FaEnrollment() {
    return api.get<User2FAEnrollmentResponse>('/users/me/2fa_enrollment');
  },

  async getSendableCompanyUsers(companyId: string) {
    return api.get<GetSendableCompanyUser[]>(
      `/companies/${companyId}/periodic_export/sendable-users`
    );
  },

  async getCompanyPeriodicExportSettings(companyId: string) {
    return api.get<GetCompanyPeriodicExportSetting[]>(
      `/companies/${companyId}/periodic_export/settings`
    );
  },

  async getCompanyPeriodicExportSettingDetail(
    companyId: string,
    settingId: number
  ) {
    return api.get<GetCompanyPeriodicExportSettingDetail>(
      `/companies/${companyId}/periodic_export/settings/${settingId}`
    );
  },

  async createCompanyPeriodicExportSetting(
    companyId: string,
    body: CreateCompanyPeriodicExportSettingRequest
  ) {
    return api.post(`/companies/${companyId}/periodic_export/settings`, body);
  },

  async updateCompanyPeriodicExportSetting(
    companyId: string,
    settingId: number,
    body: UpdateCompanyPeriodicExportSettingRequest
  ) {
    return api.patch(
      `/companies/${companyId}/periodic_export/settings/${settingId}`,
      body
    );
  },

  async deleteCompanyPeriodicExportSetting(
    companyId: string,
    settingId: number
  ) {
    return api.delete(
      `/companies/${companyId}/periodic_export/settings/${settingId}`
    );
  },

  async getClientIp() {
    return api.get('/utils/get-client-ip');
  },
  async getDynamicList(dynamicListId: number) {
    return api.get<GetDynamicList>(`/dynamic_lists/${dynamicListId}`);
  },
  async updateDynamicList(
    dynamicListId: number,
    data: UpdateDynamicListRequestBody
  ) {
    return api.put(`/dynamic_lists/${dynamicListId}`, data);
  },
  async getDynamicLists(page: number, limit: number, showAll: boolean) {
    const queryParams: { page: number; limit: number; show_all: boolean } = {
      page: page,
      limit: limit,
      show_all: showAll,
    };

    const query = qs.stringify(queryParams, { indices: false });

    return api.get<GetDynamicLists>(`/dynamic_lists?${query}`);
  },
  async getDynamicListResults(
    dynamicListId: number,
    data: GetDynamicListResultsRequestBody
  ) {
    const { sortState, ...body } = data;
    const query = qs.stringify(
      {
        ...body,
        order_by_name: sortState?.name,
        order_by_revised_at: sortState?.revised_at,
        order_by_created_at: sortState?.created_at,
        order_by_updated_at: sortState?.updated_at,
      },
      { indices: false }
    );
    return api.get<GetDynamicListResults<DynamicListResultItem>>(
      `/dynamic_list_results/${dynamicListId}?${query}`
    );
  },
  async getExportDynamicListResults(dynamicListId: number) {
    return api.get(`/dynamic_list_results/${dynamicListId}/export`, {
      responseType: 'blob',
    });
  },
  async createDynamicList(data: CreateDynamicListRequestBody) {
    return api.post('/dynamic_lists', data);
  },
  async requestDynamicList(data: RequestDynamicListRequestBody) {
    return api.post('/dynamic_lists/request', data);
  },
  async resumeDynamicLists(data: { dynamic_list_ids: number[] }) {
    return api.put('/dynamic_lists/resume', data);
  },
  async suspendDynamicLists(data: { dynamic_list_ids: number[] }) {
    return api.put('/dynamic_lists/suspend', data);
  },
  async deleteDynamicLists(data: { dynamic_list_ids: number[] }) {
    return api.delete('/dynamic_lists/delete', { data });
  },
  // SE-572 v2 通知機能: チェックリストのお気に入り(★)自己購読
  async favoriteDynamicList(dynamicListId: number) {
    return api.post(`/dynamic_lists/${dynamicListId}/favorite`);
  },
  async unfavoriteDynamicList(dynamicListId: number) {
    return api.delete(`/dynamic_lists/${dynamicListId}/favorite`);
  },
  async bulkFavoriteDynamicLists(data: {
    dynamic_list_ids: number[];
    action: 'add' | 'remove';
  }) {
    return api.post('/dynamic_lists/favorites/bulk', data);
  },

  async getDynamicListTemplates(query?: GetDynamicListTemplatesRequestQuery) {
    const queryString = query ? qs.stringify(query, { indices: false }) : '';
    return api.get<GetDynamicListTemplatesResponse>(
      `/dynamic_lists/templates${queryString ? `?${queryString}` : ''}`
    );
  },
  async getDynamicListTemplate(templateId: number) {
    return api.get<DynamicListTemplate>(
      `/dynamic_lists/templates/${templateId}`
    );
  },
  async getDynamicListTemplateCategories() {
    return api.get('/master/categories');
  },
  async createDynamicListFromTemplate(
    data: CreateDynamicListFromTemplateRequestBody
  ) {
    return api.post('/dynamic_lists/from_template', data);
  },

  async getRegulations(query?: GetRegulationsRequestQuery) {
    return api.get<Regulation[]>('/dynamic_lists/regulations', {
      params: { ids: query?.ids },
      paramsSerializer: { indexes: null },
    });
  },

  async getLaws(query?: GetLawsRequestQuery) {
    return api.get<Law[]>('/dynamic_lists/laws', {
      params: { ids: query?.ids },
      paramsSerializer: { indexes: null },
    });
  },

  async getExportCreateSimple(sdsId: string) {
    cancelTokenSource = axios.CancelToken.source();

    try {
      const response = await api.get<ArrayBuffer>(`/create_simple/${sdsId}`, {
        responseType: 'arraybuffer',
        cancelToken: cancelTokenSource.token,
      });

      return response;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request was cancelled');
      }
      throw error;
    }
  },

  async getExportCreateSimpleV3(sdsId: string) {
    cancelTokenSource = axios.CancelToken.source();

    try {
      const response = await api.get<ArrayBuffer>(
        `/create_simple/v3/${sdsId}`,
        {
          responseType: 'arraybuffer',
          cancelToken: cancelTokenSource.token,
        }
      );

      return response;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request was cancelled');
      }
      throw error;
    }
  },

  async getExportCreateSimpleV3_2(sdsId: string) {
    cancelTokenSource = axios.CancelToken.source();

    try {
      const response = await api.get<ArrayBuffer>(
        `/create_simple/v3_2/${sdsId}`,
        {
          responseType: 'arraybuffer',
          cancelToken: cancelTokenSource.token,
        }
      );

      return response;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request was cancelled');
      }
      throw error;
    }
  },

  cancelCreateSimpleExportRequest() {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('User cancelled the request');
    }
  },

  cancelCreateSimpleV3ExportRequest() {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('User cancelled the request');
    }
  },

  cancelCreateSimpleV3_2ExportRequest() {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('User cancelled the request');
    }
  },

  async createReanalysisSds(data: {
    sdsId: string;
    comment: string;
    requestType: string;
  }) {
    const sdsId = data.sdsId;
    const comment = data.comment;
    const requestType = data.requestType;
    return api.post(`/sdses/${sdsId}/reanalysis`, {
      comment,
      request_type: requestType,
    });
  },

  async putDynamicListResult(
    sdsId: string,
    dynamicListId: number,
    dynamicListResult: DynamicListResultRequestBody
  ) {
    return api.put(
      `/dynamic_list_results/${dynamicListId}/${sdsId}`,
      dynamicListResult
    );
  },

  async postDynamicListResult(sdsId: string, dynamicListId: number) {
    return api.post(`/dynamic_list_results/${dynamicListId}/${sdsId}`, {});
  },

  async deleteDynamicListResult(sdsId: string, dynamicListId: number) {
    return api.delete(`/dynamic_list_results/${dynamicListId}/${sdsId}`);
  },

  async createCompanyTag(companyId: string, data: CompanyTagCreateRequestBody) {
    return api.post<CompanyTagCreateResponse>(
      `/companies/${companyId}/tags`,
      data
    );
  },

  async bulkCreateCompanyTags(
    companyId: string,
    data: CompanyTagBulkUploadRequestBody
  ) {
    return api.post<CompanyTagBulkUploadResponse>(
      `/companies/${companyId}/tags/bulk-upload-tags`,
      data
    );
  },

  async getCompanyTag(companyId: string) {
    return api.get(`/companies/${companyId}/tags`);
  },

  async deleteCompanyTag(id: number, companyId: string) {
    return api.delete(`/companies/${companyId}/tags/${id}`);
  },

  async updateCompanyTag(
    id: number,
    companyId: string,
    data: CompanyTagUpdateRequestBody
  ) {
    return api.put<CompanyTagUpdateRequestBody>(
      `/companies/${companyId}/tags/${id}`,
      data
    );
  },

  async getUserLanguage() {
    return api.get<UserLanguageResponse>('/users/me/language');
  },

  async updateUserLanguage(data: UserLanguageUpdate) {
    return api.put<UserLanguageResponse>('/users/me/language', data);
  },

  async getEditHistories(companyId: string, sdsId: string) {
    return api.get<Array<GetEditHistoriesResponse>>(
      `/companies/${companyId}/sdses/${sdsId}/edit_histories`
    );
  },

  // 企業切り替え関連
  async getSwitchableCompanies() {
    return api.get<CompanyShowResponse[]>('/user/switchable-companies');
  },

  async getAllGroupCompanies() {
    return api.get<(CompanyShowResponse & { is_switchable: boolean })[]>(
      '/user/switchable-companies/all-group-companies'
    );
  },

  async getMSdsSection2Categories() {
    return api.get<MSdsSection2Category[]>('/master/sds_section2_categories');
  },
  async getMSdsSection2Items() {
    return api.get<MSdsSection2Item[]>('/master/sds_section2_items');
  },
  async getMSdsSection2Hazards() {
    return api.get<MSdsSection2Hazard[]>('/master/sds_section2_hazards');
  },
  async getCurrentCompanyContext() {
    return api.get<CompanyShowResponse>('/user/switchable-companies/current');
  },

  async getFeatureFlagList(user_id: string | null, company_id?: string) {
    let url = `/feature_flags?user_id=${user_id}`;
    if (company_id) {
      url += `&company_id=${company_id}`;
    }
    return publicApi.get<GetFeatureFlagListResponse>(url);
  },

  // サプライヤー問い合わせ (SUPPLIER_INQUIRY) — customer side
  async createSupplierInquiryRequest(body: SupplierInquiryCreateRequest) {
    return api.post<SupplierInquiryCreateResponse>(
      '/supplier-inquiry/requests',
      body
    );
  },
  async getSupplierInquiryRequests(
    query: GetSupplierInquiryRequestsQuery = {}
  ) {
    const qsStr = qs.stringify(query, {
      arrayFormat: 'repeat',
      skipNulls: true,
    });
    const url = qsStr
      ? `/supplier-inquiry/requests?${qsStr}`
      : '/supplier-inquiry/requests';
    return api.get<SupplierInquiryListResponse>(url);
  },
  async cancelSupplierInquiryRequest(id: string) {
    return api.post<SupplierInquiryResponse>(
      `/supplier-inquiry/requests/${id}/cancel`
    );
  },
  async downloadSupplierInquiryFile(id: string) {
    // PdM 2026-07-22: the backend now streams the attachment(s) directly
    // — a single blob when the request has one attachment, a ZIP when it
    // has more. Callers pick the save-as filename client-side (same
    // pattern as bulkDownloadSupplierInquiryFiles and the CREATE SIMPLE
    // export flows in this repo).
    return api.get<Blob>(`/supplier-inquiry/requests/${id}/download`, {
      responseType: 'blob',
    });
  },
  async bulkDownloadSupplierInquiryFiles(requestIds: string[]) {
    return api.post<Blob>(
      '/supplier-inquiry/requests/bulk-download',
      { request_ids: requestIds },
      { responseType: 'blob' }
    );
  },
  async getSupplierInquiryQuota() {
    return api.get<SupplierInquiryQuotaResponse>('/supplier-inquiry/quota');
  },
  async getSupplierInquirySummary(params?: {
    status?: string[];
    q?: string;
    requested_from?: string;
    requested_to?: string;
    obtained_from?: string;
    obtained_to?: string;
  }) {
    return api.get<SupplierInquirySummaryResponse>(
      '/supplier-inquiry/summary',
      {
        params,
        paramsSerializer: (p) => {
          const s = new URLSearchParams();
          for (const [k, v] of Object.entries(p)) {
            if (v === undefined || v === null) continue;
            if (Array.isArray(v)) v.forEach((x) => s.append(k, x));
            else s.append(k, v as string);
          }
          return s.toString();
        },
      }
    );
  },
  async getSupplierInquiryScreeningSummary() {
    return api.get<SupplierInquiryScreeningSummaryResponse>(
      '/supplier-inquiry/screening-summary'
    );
  },
};
